import { Box, Paper, Typography } from "@mui/material";
import { EditIcon, ExitIcon, SaveIcon } from "../../../../../common/icons/Icons";
import RatesTable from "./RatesTable";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fadeInMixin } from "../../../../../common/cssMixins";
import { withQueryData } from "../../../../../common/withQueryData";
import { useFormValidator } from "../../../../../common/inputs/useFormValidator";
import { useFormStatus } from "../../../../../common/inputs/useFormStatus";
import { useRatesData } from "../../../../../common/hooks/useRatesData";

const RoomRates = ({ propertyId, room, ratePlans, index, onEdit, editing, disabled, onEditCancel, onEditSuccess, onError }) => {
    const { queryResults } = useRatesData(propertyId, room.id);

    return <>
        <Paper
            sx={{
                maxWidth: "600px",
                zIndex: (theme) => {return disabled ? 'unset' : theme.zIndex.editedSection},
                ...fadeInMixin(index / 4),
            }}
        >
            <Typography sx={{ fontWeight: "bold", padding: '0px 16px', height: '48px', alignContent: 'center' }}>{room.name}</Typography>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                minHeight: 150,
                paddingTop: '10px',
                minWidth: '600px'
            }}>
                <RoomRatesContentWithQueryData
                    propertyId={propertyId}
                    queryData={queryResults}
                    room={room}
                    ratePlans={ratePlans}
                    onEdit={() => onEdit(room.id)}
                    onEditSuccess={onEditSuccess}
                    editing={editing}
                    onEditCancel={onEditCancel}
                    onError={onError}
                />

            </Box>
        </Paper >
    </>
}

const RoomRatesContent = ({ propertyId, room, ratePlans, data: rates, onEdit, onEditSuccess, editing, onEditCancel, onError }) => {
    const [valid, setValid, resetValid] = useFormValidator({});
    const [status, setStatus, statusCodes] = useFormStatus();

    const { updateAllRates } = useRatesData(propertyId, room.id);

    const ratesChanged = useRef();

    const findPriceByPlan = useCallback((planId) => {
        let price = null;
        for (let index in rates) {
            if (rates[index].planId === planId) {
                price = rates[index].price;
                break;
            }
        }
        return price;
    }, [rates]);

    const plansWithRates = useMemo(() => ratePlans && ratePlans.map((plan) => {
        const price = findPriceByPlan(plan.id);

        return {
            ...plan,
            price: price,
            enabled: !!price,
        }
    }), [ratePlans, findPriceByPlan]);

    const [editablePlans, setEditablePlans] = useState([...plansWithRates]);

    //reset editablePlans every time rates are changed
    useEffect(() => {
        setEditablePlans([...plansWithRates])
    }, [plansWithRates]);

    const onEnableRate = (id, planId, enabled) => {
        setRate(planId, enabled);

        if (!enabled) {
            setValid(id, true);
        }
    }

    const onChangeRate = (id, planId, value, valid) => {
        setValid(id, valid);

        setRate(planId, true, value);
    }

    const setRate = (planId, enabled, price) => {
        let needsUpdate = false;

        const updatedData = editablePlans.map(plan => {
            let planData = { ...plan };
            if (plan.id === planId) {
                const newPrice = price !== undefined ? price : (plan.price || 100);
                if (planData.enabled !== enabled || planData.price !== newPrice) {
                    needsUpdate = true;

                    planData.enabled = enabled;
                    planData.price = newPrice;
                }
            }
            return planData;
        });

        if (needsUpdate) {
            setEditablePlans(updatedData);
            ratesChanged.current = true;
        }
    }

    const cancelEditing = () => {
        setEditablePlans([...plansWithRates]);
        ratesChanged.current = false;
        resetValid();
        onEditCancel();
    }

    const onSubmit = async () => {
        setStatus(statusCodes.SAVING);

        const planRates = editablePlans
            .filter(plan => plan.enabled)
            .map(plan => ({ planId: plan.id, price: plan.price }));

        try {
            const updatedPlans = await updateAllRates(planRates);

            setStatus(statusCodes.AWAITING_USER_INPUT);
            ratesChanged.current = false;
            onEditSuccess(updatedPlans);
        }
        catch (error) {
            setStatus(statusCodes.AWAITING_USER_INPUT);
            onError(error);
        }
    }

    const actionButtons = !editing ? <EditIcon onClick={onEdit} /> : <>{ratesChanged.current && valid && <SaveIcon loading={status === statusCodes.SAVING} onClick={onSubmit} />}<ExitIcon onClick={cancelEditing} disabled={status === statusCodes.SAVING} /></>

    return <>
        <Box sx={{ display: "flex", padding: '0px 16px' }}><Box sx={{ flexGrow: 1 }} />{actionButtons}</Box>
        <Box sx={{ position: 'relative', minHeight: '100px', padding: '8px 16px' }}>
            <RatesTable data={editablePlans} editable={editing} onEnableRate={onEnableRate} onChangeRate={onChangeRate} roomId={room.id} disabled={status === statusCodes.SAVING} />
        </Box>
    </>
}

const RoomRatesContentWithQueryData = withQueryData(RoomRatesContent);

export default RoomRates;